/**
 * UnifiedFooter Component
 *
 * Full footer with multiple sections: Thông tin, Hỗ trợ, Liên hệ
 * Matches design requirements with complete content
 *
 * This footer is used across all screens for consistent branding
 */

import { Link } from 'react-router-dom';
import { Globe, Mail, Facebook, Youtube, Users } from 'lucide-react';
import { BidvPromoBanner, FeedbackWidget } from '@vsat/shared/components';

export function UnifiedFooter() {
  // Logo images - local files
  const logoUrl = '/logo-dinh-huong-nghe-nghiep.png';
  const cognotivLogoUrl = '/logo-cognotiv.png';
  const boCongThuongLogoUrl = '/dathongbaobocongthuong.png';

  return (
    <footer className='bg-gray-50 dark:bg-[#1E293B] pt-12 pb-8 border-t border-gray-200 dark:border-gray-700'>
      <div className='max-w-7xl mx-auto px-4 sm:px-6 lg:px-8'>
        {/* Main Footer Content - Grid Layout */}
        <div className='grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-8 lg:gap-12 mb-10'>
          {/* Column 1: Logo and Branding */}
          <div className='sm:col-span-2 lg:col-span-1'>
            <div className='flex items-center gap-3 mb-4'>
              <div className='w-12 h-12 flex items-center justify-center overflow-hidden'>
                <img
                  alt='Logo Viện Footer'
                  className='w-full h-full object-contain'
                  src={logoUrl}
                  onError={e => {
                    const target = e.currentTarget;
                    target.style.display = 'none';
                    if (target.parentElement) {
                      target.parentElement.innerHTML =
                        '<span class="material-symbols-outlined text-primary text-xl">school</span>';
                    }
                  }}
                />
              </div>
              <div>
                <p className='text-sm font-bold text-gray-900 dark:text-white'>Hướng nghiệp CAT</p>
                <p className='text-xs text-gray-500 dark:text-gray-400'>Career Aptitude Test</p>
              </div>
            </div>
            <p className='text-sm text-gray-600 dark:text-gray-400 leading-relaxed'>
              Công cụ đánh giá và định hướng nghề nghiệp thông minh dành cho học sinh THPT/THCS.
            </p>
          </div>

          {/* Column 2: Thông tin */}
          <div>
            <h4 className='text-sm font-bold text-gray-900 dark:text-white mb-4'>Thông tin</h4>
            <ul className='space-y-3'>
              <li>
                <Link
                  to='/lich-su-danh-gia'
                  className='text-sm text-gray-600 dark:text-gray-400 hover:text-primary dark:hover:text-primary transition-colors inline-flex items-center gap-2'
                >
                  Tổng quan
                </Link>
              </li>
              <li>
                <Link
                  to='/huong-nghiep-cat?screen=intro'
                  className='text-sm text-gray-600 dark:text-gray-400 hover:text-primary dark:hover:text-primary transition-colors inline-flex items-center gap-2'
                >
                  Làm bài Hướng nghiệp CAT
                </Link>
              </li>
              <li>
                <Link
                  to='/'
                  className='text-sm text-gray-600 dark:text-gray-400 hover:text-primary dark:hover:text-primary transition-colors inline-flex items-center gap-2'
                >
                  Về chúng tôi
                </Link>
              </li>
            </ul>
          </div>

          {/* Column 3: Chính sách */}
          <div>
            <h4 className='text-sm font-bold text-gray-900 dark:text-white mb-4'>Chính sách</h4>
            <ul className='space-y-3'>
              <li>
                <Link
                  to='/gioi-thieu'
                  className='text-sm text-gray-600 dark:text-gray-400 hover:text-primary dark:hover:text-primary transition-colors'
                >
                  Giới thiệu
                </Link>
              </li>
              <li>
                <Link
                  to='/chinh-sach-thanh-toan'
                  className='text-sm text-gray-600 dark:text-gray-400 hover:text-primary dark:hover:text-primary transition-colors'
                >
                  Chính sách thanh toán
                </Link>
              </li>
              <li>
                <Link
                  to='/chinh-sach-giao-nhan'
                  className='text-sm text-gray-600 dark:text-gray-400 hover:text-primary dark:hover:text-primary transition-colors'
                >
                  Chính sách giao nhận
                </Link>
              </li>
              <li>
                <Link
                  to='/chinh-sach-hoan-tien'
                  className='text-sm text-gray-600 dark:text-gray-400 hover:text-primary dark:hover:text-primary transition-colors'
                >
                  Chính sách hoàn tiền
                </Link>
              </li>
              <li>
                <Link
                  to='/chinh-sach-bao-mat'
                  className='text-sm text-gray-600 dark:text-gray-400 hover:text-primary dark:hover:text-primary transition-colors'
                >
                  Chính sách bảo mật
                </Link>
              </li>
            </ul>
          </div>

          {/* Column 4: Liên hệ & Mạng xã hội */}
          <div>
            <h4 className='text-sm font-bold text-gray-900 dark:text-white mb-4'>Liên hệ</h4>
            <ul className='space-y-3 mb-6'>
              <li>
                <a
                  href='https://www.cat.edu.vn/'
                  target='_blank'
                  rel='noopener noreferrer'
                  className='text-sm text-gray-600 dark:text-gray-400 hover:text-primary dark:hover:text-primary transition-colors inline-flex items-center gap-2'
                >
                  <Globe className='h-4 w-4' />
                  www.cat.edu.vn
                </a>
              </li>
              <li>
                <a
                  className='text-sm text-gray-600 dark:text-gray-400 hover:text-primary dark:hover:text-primary transition-colors inline-flex items-center gap-2'
                  href='mailto:support@cat.edu.vn'
                >
                  <Mail className='h-4 w-4' />
                  support@cat.edu.vn
                </a>
              </li>
            </ul>

            {/* Social Links */}
            <h4 className='text-sm font-bold text-gray-900 dark:text-white mb-3'>Kết nối</h4>
            <div className='flex items-center gap-2'>
              <a
                href='https://www.facebook.com/IEQTE.CAT/'
                target='_blank'
                rel='noopener noreferrer'
                className='w-9 h-9 bg-blue-100 dark:bg-blue-900/30 rounded-lg flex items-center justify-center text-blue-600 dark:text-blue-400 hover:bg-blue-200 dark:hover:bg-blue-800/40 transition-all hover:scale-105'
                aria-label='Facebook'
              >
                <Facebook className='h-4 w-4' />
              </a>
              <a
                href='https://www.facebook.com/groups/IEQTE.CAT/'
                target='_blank'
                rel='noopener noreferrer'
                className='w-9 h-9 bg-blue-100 dark:bg-blue-900/30 rounded-lg flex items-center justify-center text-blue-600 dark:text-blue-400 hover:bg-blue-200 dark:hover:bg-blue-800/40 transition-all hover:scale-105'
                aria-label='Facebook Group'
              >
                <Users className='h-4 w-4' />
              </a>
              <a
                href='https://www.youtube.com/@ieqtecat'
                target='_blank'
                rel='noopener noreferrer'
                className='w-9 h-9 bg-red-100 dark:bg-red-900/30 rounded-lg flex items-center justify-center text-red-600 dark:text-red-400 hover:bg-red-200 dark:hover:bg-red-800/40 transition-all hover:scale-105'
                aria-label='YouTube'
              >
                <Youtube className='h-4 w-4' />
              </a>
              <a
                href='https://www.tiktok.com/@ieqte.cat'
                target='_blank'
                rel='noopener noreferrer'
                className='w-9 h-9 bg-gray-200 dark:bg-gray-700 rounded-lg flex items-center justify-center text-gray-700 dark:text-gray-300 hover:bg-gray-300 dark:hover:bg-gray-600 transition-all hover:scale-105'
                aria-label='TikTok'
              >
                <svg className='h-4 w-4' viewBox='0 0 24 24' fill='currentColor'>
                  <path d='M19.59 6.69a4.83 4.83 0 01-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 01-5.2 1.74 2.89 2.89 0 012.31-4.64 2.93 2.93 0 01.88.13V9.4a6.84 6.84 0 00-1-.05A6.33 6.33 0 005 20.1a6.34 6.34 0 0010.86-4.43v-7a8.16 8.16 0 004.77 1.52v-3.4a4.85 4.85 0 01-1-.1z' />
                </svg>
              </a>
            </div>
          </div>
        </div>

        {/* BIDV Promotional Banner */}
        <BidvPromoBanner variant='compact' className='mt-4' />

        {/* Partnership Section */}
        <div className='border-t border-gray-200 dark:border-gray-700 pt-6 mb-6'>
          {/* Bộ Công Thương Logo */}
          <div className='flex justify-center mb-6'>
            <a
              href='http://online.gov.vn/Home/WebDetails/139139'
              target='_blank'
              rel='noopener noreferrer'
              className='inline-block hover:opacity-80 transition-opacity'
              aria-label='Đã thông báo Bộ Công Thương'
            >
              <img
                src={boCongThuongLogoUrl}
                alt='Đã thông báo Bộ Công Thương'
                className='h-16 w-auto object-contain'
              />
            </a>
          </div>

          <div className='flex flex-col lg:flex-row items-center justify-center gap-6 lg:gap-10'>
            {/* COGNOTIV */}
            <div className='flex items-center gap-3'>
              <div className='w-12 h-12 flex-shrink-0 bg-white dark:bg-gray-700 rounded-lg p-1.5 shadow-sm border border-gray-200 dark:border-gray-600'>
                <img
                  alt='Cognotiv Vietnam'
                  className='w-full h-full object-contain'
                  src={cognotivLogoUrl}
                />
              </div>
              <div className='text-left'>
                <p className='text-xs text-gray-500 dark:text-gray-400'>Triển khai & vận hành</p>
                <p className='text-sm font-semibold text-gray-800 dark:text-gray-200'>
                  COGNOTIV VIỆT NAM
                </p>
              </div>
            </div>

            {/* Divider */}
            <div className='hidden lg:block h-10 w-px bg-gray-300 dark:bg-gray-600'></div>
            <span className='lg:hidden text-gray-400'>×</span>

            {/* IEQTE */}
            <div className='flex items-center gap-3'>
              <div className='w-12 h-12 flex-shrink-0 bg-white dark:bg-gray-700 rounded-lg p-1.5 shadow-sm border border-gray-200 dark:border-gray-600'>
                <img alt='IEQTE' className='w-full h-full object-contain' src={logoUrl} />
              </div>
              <div className='text-left'>
                <p className='text-xs text-gray-500 dark:text-gray-400'>
                  Bản quyền phương pháp CAT
                </p>
                <p className='text-sm font-semibold text-gray-800 dark:text-gray-200'>
                  VIỆN KHẢO THÍ IEQTE
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Copyright Section */}
        <div className='border-t border-gray-200 dark:border-gray-700 pt-4'>
          <div className='flex flex-col md:flex-row justify-between items-center gap-3'>
            <p className='text-xs text-gray-500 dark:text-gray-400 text-center md:text-left'>
              © 2025 Cognotiv Việt Nam. All rights reserved.
            </p>
            <div className='flex items-center gap-4 text-xs text-gray-500 dark:text-gray-400'>
              <FeedbackWidget systemContext='THPT' variant='inline' />
              <span>•</span>
              <Link to='/terms' className='hover:text-primary transition-colors'>
                Điều khoản
              </Link>
              <span>•</span>
              <Link to='/privacy' className='hover:text-primary transition-colors'>
                Chính sách
              </Link>
              <span>•</span>
              <a
                href='https://www.cat.edu.vn/'
                target='_blank'
                rel='noopener noreferrer'
                className='hover:text-primary transition-colors'
              >
                cat.edu.vn
              </a>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
}
